/**
 * @fileoverview Clever Instant Login callback page (AR-925)
 * @description Clever redirects here with `?code=` (or `?error=` when the
 * user denied access). The code is posted to the backend once; on success the
 * user is stored the same way every other login does (`useAuth().login`) and
 * the browser leaves for the page the visitor wanted. OAuth codes are
 * single-use, so the exchange is guarded against StrictMode double effects.
 */

import { Button, Loader, Stack, Text } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'

import { ArcAuthHeader } from 'components/ArcAuthHeader'
import { ArcAuthShell } from 'components/ArcAuthPage'
import { useAuth } from 'context/userAuthContext'
import { ArcTitle, FormCard, FormColumn, Subtitle } from 'pages/SignIn/ArcSignIn/styles'
import { CleverSigninFailure, cleverSignin, consumePostLoginUrl } from 'services/cleverSso'

type CallbackFailure = CleverSigninFailure | 'denied' | 'missing_code'

const ERROR_COLOR = '#fa5252'

export const CleverCallback = () => {
  const [searchParams] = useSearchParams()
  const { login } = useAuth()
  const { t } = useTranslation()
  const [failure, setFailure] = useState<CallbackFailure | null>(null)
  const startedRef = useRef(false)

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true

    if (searchParams.get('error')) {
      setFailure('denied')
      return
    }
    const code = searchParams.get('code')
    if (!code) {
      setFailure('missing_code')
      return
    }

    cleverSignin(code).then(result => {
      if (!result.success) {
        setFailure(result.reason)
        return
      }
      // Writes the `user` localStorage entry and applies the user's locale
      login(result.user)
      window.location.href = consumePostLoginUrl()
    })
  }, [])

  return (
    <ArcAuthShell>
      <ArcAuthHeader />
      <FormColumn>
        <ArcTitle>
          {t('arc_signin.title_prefix')} Arc<span>Mosaico</span>
        </ArcTitle>
        <Subtitle>{failure ? t('clever_sso.failed_subtitle') : t('clever_sso.signing_in_subtitle')}</Subtitle>
        <FormCard>
          <Stack align='center' spacing='md'>
            {failure ? (
              <>
                <IconAlertCircle size={56} color={ERROR_COLOR} stroke={1.5} />
                <Text color='dimmed' size='sm' align='center'>
                  {t(`clever_sso.errors.${failure}`)}
                </Text>
                <Button component={Link} to='/account/signin' variant='outline' size='lg' fullWidth mt='sm'>
                  {t('clever_sso.back_to_login')}
                </Button>
              </>
            ) : (
              <>
                <Loader size='xl' />
                <Text color='dimmed' size='sm'>
                  {t('clever_sso.signing_in_message')}
                </Text>
              </>
            )}
          </Stack>
        </FormCard>
      </FormColumn>
    </ArcAuthShell>
  )
}
